// IndexedDB wrapper for client-side storage

import { type IDBPDatabase, openDB } from "idb";
import type { Preset } from "../interfaces/preset-management.js";
import type {
	AudioFile,
	Session,
	SessionSummary,
	UserPreferences,
} from "../types/core.js";
import type { MonologDB, StoredAudioFile } from "./indexeddb-schema.js";
import { AudioRepository } from "./repositories/audio-repository.js";
import { PreferenceRepository } from "./repositories/preference-repository.js";
import { SessionRepository } from "./repositories/session-repository.js";

export class IndexedDBWrapper {
	private db: IDBPDatabase<MonologDB> | null = null;
	private readonly dbName = "monolog-live";
	private readonly version = 3;
	private initializationPromise: Promise<void> | null = null;

	private readonly sessionRepository = new SessionRepository(
		this.withDb.bind(this),
	);
	private readonly audioRepository = new AudioRepository(
		this.withDb.bind(this),
	);
	private readonly preferenceRepository = new PreferenceRepository(
		this.withDb.bind(this),
	);

	async initialize(): Promise<void> {
		if (this.initializationPromise) {
			return this.initializationPromise;
		}

		if (this.db) {
			return Promise.resolve();
		}

		this.initializationPromise = this.performInitialization();
		return this.initializationPromise;
	}

	private async performInitialization(): Promise<void> {
		try {
			this.db = await openDB<MonologDB>(this.dbName, this.version, {
				upgrade: async (db, oldVersion, _newVersion, _transaction) => {
					if (oldVersion < 1) {
						const sessionStore = db.createObjectStore("sessions", {
							keyPath: "id",
						});
						sessionStore.createIndex("by-user", "userId");
						sessionStore.createIndex("by-date", "startTime");

						db.createObjectStore("preferences", { keyPath: "userId" });

						const audioStore = db.createObjectStore("audioFiles", {
							keyPath: "id",
						});
						audioStore.createIndex("by-session", "sessionId");
						audioStore.createIndex("by-date", "createdAt");

						const summaryStore = db.createObjectStore("summaries", {
							keyPath: "sessionId",
						});
						summaryStore.createIndex("by-session", "sessionId");
					}

					if (oldVersion < 3 && !db.objectStoreNames.contains("presets")) {
						db.createObjectStore("presets", { keyPath: "id" });
					}

					// v3 への移行時にのみ legacy フィールドを削除する
					if (
						oldVersion >= 1 &&
						oldVersion < 3 &&
						db.objectStoreNames.contains("sessions")
					) {
						await this.migrateEnhancedTranscriptionField(db);
					}
				},
			});
		} catch (error) {
			console.warn("IndexedDB initialization failed:", error);
			this.db = null;
		}
	}

	private async migrateEnhancedTranscriptionField(
		db: IDBPDatabase<MonologDB>,
	): Promise<void> {
		try {
			const tx = db.transaction("sessions", "readwrite");
			const store = tx.objectStore("sessions");
			let cursor = await store.openCursor();

			let migrationCount = 0;
			while (cursor) {
				const session = cursor.value;
				if ("enhancedTranscription" in session) {
					delete (session as { enhancedTranscription?: unknown })
						.enhancedTranscription;
					await cursor.update(session);
					migrationCount++;
				}
				cursor = await cursor.continue();
			}

			if (migrationCount > 0) {
				console.log(
					`[Migration] Removed enhancedTranscription from ${migrationCount} sessions`,
				);
			}

			await tx.done;
		} catch (error) {
			console.warn("Data migration failed:", error);
		}
	}

	private async ensureInitialized(): Promise<void> {
		if (!this.db && !this.initializationPromise) {
			await this.initialize();
		} else if (this.initializationPromise) {
			await this.initializationPromise;
		}
	}

	private async withDb<T>(
		onUnavailableMessage: string,
		fallback: () => T | Promise<T>,
		operation: (db: IDBPDatabase<MonologDB>) => Promise<T>,
	): Promise<T> {
		await this.ensureInitialized();
		if (!this.db) {
			console.warn(onUnavailableMessage);
			return fallback();
		}
		return operation(this.db);
	}

	isInitialized(): boolean {
		return !!this.db;
	}

	// Session operations
	async saveSession(session: Session): Promise<void> {
		return this.sessionRepository.saveSession(session);
	}

	async getSession(sessionId: string): Promise<Session | undefined> {
		return this.sessionRepository.getSession(sessionId);
	}

	async getSessionsByUser(userId: string): Promise<Session[]> {
		return this.sessionRepository.getSessionsByUser(userId);
	}

	async updateSessionMetadata(
		sessionId: string,
		metadata: { title?: string; isFavorite?: boolean },
	): Promise<void> {
		return this.sessionRepository.updateSessionMetadata(sessionId, metadata);
	}

	async updateSessionTranscript(
		sessionId: string,
		transcript: Session["transcript"],
	): Promise<void> {
		return this.sessionRepository.updateSessionTranscript(
			sessionId,
			transcript,
		);
	}

	async deleteSession(sessionId: string): Promise<void> {
		return this.sessionRepository.deleteSession(sessionId);
	}

	async deleteSessions(sessionIds: string[]): Promise<void> {
		return this.sessionRepository.deleteSessions(sessionIds);
	}

	// User preferences operations
	async savePreferences(
		userId: string,
		preferences: UserPreferences,
	): Promise<void> {
		return this.preferenceRepository.savePreferences(userId, preferences);
	}

	async getPreferences(userId: string): Promise<UserPreferences | undefined> {
		return this.preferenceRepository.getPreferences(userId);
	}

	// Audio file operations
	async saveAudioFile(audioFile: AudioFile & { blob?: Blob }): Promise<void> {
		return this.audioRepository.saveAudioFile(audioFile);
	}

	async getAudioFile(
		fileId: string,
	): Promise<(AudioFile & { blob?: Blob }) | undefined> {
		return this.audioRepository.getAudioFile(fileId);
	}

	async getAudioFilesBySession(
		sessionId: string,
	): Promise<(AudioFile & { blob?: Blob })[]> {
		return this.audioRepository.getAudioFilesBySession(sessionId);
	}

	async deleteAudioFile(fileId: string): Promise<void> {
		return this.audioRepository.deleteAudioFile(fileId);
	}

	async getAllAudioFiles(): Promise<StoredAudioFile[]> {
		return this.audioRepository.getAllAudioFiles();
	}

	async getAllSessions(): Promise<Session[]> {
		return this.sessionRepository.getAllSessions();
	}

	async getAllPreferences(): Promise<UserPreferences[]> {
		return this.preferenceRepository.getAllPreferences();
	}

	async getAllSummaries(): Promise<SessionSummary[]> {
		return this.withDb(
			"Database not available, returning empty array",
			() => [],
			async (db) => db.getAll("summaries"),
		);
	}

	async getAudioStorageStats(): Promise<{
		totalFiles: number;
		totalSize: number;
		oldestFile: Date | null;
		newestFile: Date | null;
	}> {
		return this.audioRepository.getAudioStorageStats();
	}

	// Summary operations
	async saveSummary(summary: SessionSummary): Promise<void> {
		return this.withDb(
			"Database not available, skipping summary save",
			() => undefined,
			async (db) => {
				await db.put("summaries", summary);
			},
		);
	}

	async getSummary(sessionId: string): Promise<SessionSummary | undefined> {
		return this.withDb(
			"Database not available, returning undefined for summary",
			() => undefined,
			async (db) => db.get("summaries", sessionId),
		);
	}

	// Preset operations
	async savePreset(preset: Preset): Promise<void> {
		return this.withDb(
			"Database not available, skipping preset save",
			() => undefined,
			async (db) => {
				await db.put("presets", preset);
			},
		);
	}

	async getPreset(id: string): Promise<Preset | undefined> {
		return this.withDb(
			"Database not available, returning undefined for preset",
			() => undefined,
			async (db) => db.get("presets", id),
		);
	}

	async getAllPresets(): Promise<Preset[]> {
		return this.withDb(
			"Database not available, returning empty presets array",
			() => [],
			async (db) => db.getAll("presets"),
		);
	}

	async deletePreset(id: string): Promise<void> {
		return this.withDb(
			"Database not available, skipping preset delete",
			() => undefined,
			async (db) => {
				await db.delete("presets", id);
			},
		);
	}

	// Utility operations
	async clearAllData(): Promise<void> {
		return this.withDb(
			"Database not available, skipping data clear",
			() => undefined,
			async (db) => {
				const tx = db.transaction(
					["sessions", "preferences", "audioFiles", "summaries", "presets"],
					"readwrite",
				);
				await Promise.all([
					tx.objectStore("sessions").clear(),
					tx.objectStore("preferences").clear(),
					tx.objectStore("audioFiles").clear(),
					tx.objectStore("summaries").clear(),
					tx.objectStore("presets").clear(),
				]);
			},
		);
	}

	async getStorageEstimate(): Promise<{ usage: number; quota: number }> {
		if ("storage" in navigator && "estimate" in navigator.storage) {
			const estimate = await navigator.storage.estimate();
			return {
				usage: estimate.usage || 0,
				quota: estimate.quota || 0,
			};
		}
		return { usage: 0, quota: 0 };
	}

	async close(): Promise<void> {
		if (this.db) {
			this.db.close();
			this.db = null;
		}
	}

	// Backward compatibility methods for tests
	async saveUserPreferences(
		userId: string,
		preferences: UserPreferences,
	): Promise<void> {
		return this.savePreferences(userId, preferences);
	}

	async getUserPreferences(
		userId: string,
	): Promise<UserPreferences | undefined> {
		return this.getPreferences(userId);
	}
}
